/**
 * Canonical margin-quality thresholds, shared by every module that
 * paints a margin % with a danger/warning/success tone (Margins,
 * Product↔Customer Pricing, and `<MarginIndicator>` itself).
 *
 * Before this module existed, MarginIndicator, MarginsPage, and the
 * Product↔Customer Pricing module each hardcoded their own cutoffs
 * and disagreed (15/25 vs. a flat 20 vs. 15/30). Product decision:
 * adopt MarginIndicator's original default — { danger: 15, warning:
 * 25 } — as the one set used everywhere.
 *
 * `getMarginThresholds` takes an optional product-category param as
 * a seam for a planned specialty-vs-commodity split — it's ignored
 * today (flat thresholds for every category) so call sites don't
 * need to change again once category-awareness lands.
 */
export interface MarginThresholds {
  /** Below this is danger. */
  danger: number
  /** Below this and ≥ danger is warning. At or above is success. */
  warning: number
}

const DEFAULT_THRESHOLDS: MarginThresholds = { danger: 15, warning: 25 }

// `_category` follows this repo's `^_` intentionally-unused convention
// (see eslint.config.js argsIgnorePattern) — seam for a future
// specialty-vs-commodity split, ignored today.
export function getMarginThresholds(_category?: string): MarginThresholds {
  return DEFAULT_THRESHOLDS
}

export type MarginTone = 'negative' | 'danger' | 'warning' | 'success'

/**
 * Classifies a margin % (e.g. 34.2 for 34.2%) into a semantic tone.
 * Negative values always paint as `'negative'` regardless of
 * thresholds.
 */
export function classifyMargin(
  value: number,
  thresholds: MarginThresholds = getMarginThresholds(),
): MarginTone {
  if (value < 0) return 'negative'
  if (value < thresholds.danger) return 'danger'
  if (value < thresholds.warning) return 'warning'
  return 'success'
}
